/**
 * ProtectedRoute Component
 *
 * Route guard that checks RBAC permissions before rendering a route.
 * Use this to protect entire pages/routes based on segment access.
 *
 * DESIGN DECISION: When access is denied, redirect to dashboard (not AccessDenied page).
 * Dashboard is the user's homepage and always accessible. This avoids dead-end pages.
 *
 * Usage:
 *   // Protect a billing page
 *   <Route
 *     path="/billing/credits"
 *     element={
 *       <ProtectedRoute segment="billing" minLevel="admin">
 *         <CrumbRoute title="Credits">
 *           <CreditsPage />
 *         </CrumbRoute>
 *       </ProtectedRoute>
 *     }
 *   />
 *
 *   // With custom redirect
 *   <ProtectedRoute
 *     segment="settings"
 *     minLevel="admin"
 *     redirectTo="/custom-page"
 *   >
 *     <SettingsPage />
 *   </ProtectedRoute>
 *
 * @see docs/projects/rbac/03-design-doc.md Section 9 - Frontend Navigation & Permission UX
 */

import React, { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { usePermissions } from '../../hooks/usePermissions'
import { BusinessSegment, FunctionRole } from '../../types/rbac'

interface ProtectedRouteProps {
  /**
   * Single segment to check
   */
  segment?: BusinessSegment

  /**
   * Minimum access level required (default: 'viewer')
   */
  minLevel?: FunctionRole

  /**
   * Multiple segment/level pairs - user must have ALL permissions
   */
  segments?: [BusinessSegment, FunctionRole][]

  /**
   * Content to render if permission check passes
   */
  children: ReactNode

  /**
   * Require the current user to be a warehouse user in addition to RBAC access.
   */
  warehouseOnly?: boolean

  /**
   * Custom redirect path if access denied.
   * Default: user's dashboard (/warehouse/dashboard/ or /company/dashboard/)
   */
  redirectTo?: string

  /**
   * Custom fallback component instead of redirect
   */
  fallback?: ReactNode
}

export function ProtectedRoute({
  segment,
  minLevel = 'viewer',
  segments,
  children,
  warehouseOnly = false,
  redirectTo,
  fallback,
}: ProtectedRouteProps): React.ReactElement | null {
  const { hasAccess, isWarehouseUser } = usePermissions()
  const location = useLocation()

  let hasPermission: boolean

  if (segments && segments.length > 0) {
    // Multiple segments - user must have ALL permissions
    hasPermission = segments.every(([seg, level]) => hasAccess(seg, level))
  } else if (segment) {
    // Single segment check
    hasPermission = hasAccess(segment, minLevel)
  } else {
    // No segment specified - allow by default
    hasPermission = true
  }

  hasPermission = (!warehouseOnly || isWarehouseUser) && hasPermission

  if (hasPermission) {
    return <>{children}</>
  }

  // Access denied - show fallback or redirect
  if (fallback) {
    return <>{fallback}</>
  }

  // Determine redirect target: use custom redirectTo, or default to user's dashboard
  const defaultDashboard = isWarehouseUser ? '/warehouse/dashboard/' : '/company/dashboard/'
  const targetPath = redirectTo ?? defaultDashboard

  // Redirect to dashboard with context (for potential toast/notification)
  return (
    <Navigate
      to={targetPath}
      state={{
        from: location.pathname,
        accessDenied: true,
        required: segment ? { segment, minLevel } : segments ? { segments } : undefined,
      }}
      replace
    />
  )
}

export default ProtectedRoute
